import { useContext } from 'react';

// Types
import type { Header, RowData } from '@tanstack/react-table';
import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Components
import Icon from '@/components/base/Icon';
import { CompoundTableContext } from '@/components/base/Table/CompoundTable';
import DefaultPopover, {
    DefaultPopoverItemType
} from '@/components/composite/Popover/DefaultPopover';

function SortItem({ text, isActivated }: { text: string; isActivated: boolean }) {
    return (
        <span className="flex items-center space-x-2">
            <span>{text}</span>
            {isActivated ? <Icon icon="check" className="text-sm" /> : null}
        </span>
    );
}

type OrderType = 'asc' | 'desc' | null;

function TableHeaderOptionsButton<TData extends RowData, TValue>({
    header
}: {
    header: Header<TData, TValue>;
}) {
    const { table } = useContext(CompoundTableContext);
    const { t } = useTranslation('commons');
    const customSorting = table.options.meta?.customSorting;
    const columnPopoverAdditionalOptions = header.column.columnDef.meta?.additionalOptions ?? [];
    const popoverItemsSort = [] as DefaultPopoverItemType[];
    const columnSort = header.column.getIsSorted();

    function isSortColumnActivated(order: OrderType) {
        if (customSorting) {
            return header?.id === customSorting.column && order === customSorting.order;
        }

        return columnSort === order;
    }

    function sortColumn(order: OrderType) {
        const isActivated = isSortColumnActivated(order);
        if (customSorting) {
            if (isActivated) {
                return customSorting.setSortColumn(null);
            }

            return customSorting.setSortColumn({ column: header.id, order });
        }

        if (isActivated) {
            return header.column.clearSorting();
        }

        header.column.toggleSorting(order === 'desc');
    }

    if (header.column.getCanHide()) {
        popoverItemsSort.push({
            click: () => {
                // Reset sort if column is sorted
                if (customSorting && customSorting?.column === header.id) {
                    customSorting.setSortColumn(null);
                } else if (columnSort) {
                    header.column.clearSorting();
                }

                header.column.toggleVisibility(false);
            },
            icon: 'invisible',
            content: t('hide')
        });
    }

    if (header.column.getCanSort()) {
        const isSortAscActivated = isSortColumnActivated('asc');
        popoverItemsSort.push({
            click: () => sortColumn('asc'),
            isActive: isSortAscActivated,
            icon: 'sort-by',
            content: <SortItem text={t('table_sort_asc_order')} isActivated={isSortAscActivated} />
        });

        const isSortDescActivated = isSortColumnActivated('desc');
        popoverItemsSort.push({
            click: () => sortColumn('desc'),
            isActive: isSortDescActivated,
            icon: 'sort-by',
            content: (
                <SortItem text={t('table_sort_desc_order')} isActivated={isSortDescActivated} />
            )
        });
    }

    const popoverItems = [...popoverItemsSort, ...columnPopoverAdditionalOptions];

    return (
        <DefaultPopover<HTMLButtonElement>
            width="auto"
            hasArrow={false}
            hasBtnClose={false}
            popoverItems={[...popoverItemsSort, ...columnPopoverAdditionalOptions]}>
            {({ setReferenceElement, togglePopover, isPopoverOpen }) => (
                <button
                    ref={setReferenceElement}
                    onClick={togglePopover}
                    data-testid={`compound-table-button-popover-option-column-${header.id}`}
                    className={clsx(
                        'flex items-center justify-center text-icoGray-500 dark:text-icoDarkMode-wolf',
                        {
                            'group-hover:visible': popoverItems.length,
                            invisible: !isPopoverOpen || !popoverItems.length
                        }
                    )}>
                    <Icon icon="dots" />
                </button>
            )}
        </DefaultPopover>
    );
}

export default TableHeaderOptionsButton;
